import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { showToastr } from '../ToastrMessage/Toast.helper';
import './DocumentVerification.styles.scss';

const getServices = () => {
  try { return JSON.parse(localStorage.getItem('cscServices') || '[]'); }
  catch { return []; }
};

const getUserDocs = () => {
  try { return JSON.parse(localStorage.getItem('userDocuments') || '{}'); }
  catch { return {}; }
};

const saveUserDocs = (docs) => localStorage.setItem('userDocuments', JSON.stringify(docs));

const docToId = (doc) => doc.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();

const MAX_PDF_BYTES = 1 * 1024 * 1024; // 1 MB hard cap for PDFs

// Compress an image to JPEG ≤ 1 200 px on the longest side at 75 % quality.
const compressImage = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1200 / img.width, 1200 / img.height, 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.75));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

const todayStr = () => new Date().toISOString().split('T')[0];

// YYYY-MM-DD → DD/MM/YYYY for display
const fmtDate = (val) => {
  if (!val) return '';
  const p = String(val).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : val;
};

const DocumentVerification = () => {
  const { applicationName } = useParams();
  const navigate = useNavigate();

  const [userDocs, setUserDocs] = useState(getUserDocs);
  const [uploading, setUploading] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileRefs = useRef({});

  const serviceName = decodeURIComponent(applicationName || '');

  const service = useMemo(
    () => getServices().find((s) => s.serviceName === serviceName) || null,
    [serviceName]
  );
  const requiredDocs = useMemo(() => service?.requiredDocuments || [], [service]);

  // A service is "closed" once its end/last date is in the past.
  const isClosed = useMemo(
    () => !!(service?.endDate && service.endDate < todayStr()),
    [service]
  );

  const uploadedCount = requiredDocs.filter((doc) => userDocs[doc]).length;
  const totalCount = requiredDocs.length;
  const allUploaded = totalCount > 0 && uploadedCount === totalCount;
  const progressPct = totalCount > 0 ? Math.round((uploadedCount / totalCount) * 100) : 100;

  const handleDelete = (docName) => {
    const updated = { ...getUserDocs() };
    delete updated[docName];
    setUserDocs(updated);
    saveUserDocs(updated);
    showToastr('success', `${docName} removed.`);
  };

  const handleFileSelect = async (docName, file) => {
    if (!file) return;
    const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
    const isPdf = file.type === 'application/pdf';
    if (!isImage && !isPdf) {
      showToastr('error', 'Only PDF, JPG, or PNG files are allowed.');
      return;
    }
    if (isPdf && file.size > MAX_PDF_BYTES) {
      showToastr('error', 'PDF must be under 1 MB. Please compress it and retry.');
      return;
    }

    setUploading((p) => ({ ...p, [docName]: 0 }));

    // Simulated progress animation (runs in parallel with file processing)
    const progressPromise = new Promise((resolve) => {
      let prog = 0;
      const iv = setInterval(() => {
        prog += 20;
        setUploading((p) => ({ ...p, [docName]: Math.min(prog, 100) }));
        if (prog >= 100) { clearInterval(iv); resolve(); }
      }, 120);
    });

    const contentPromise = isImage
      ? compressImage(file)
      : new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = (e) => res(e.target.result);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });

    const clearProgress = () =>
      setUploading((p) => { const { [docName]: _, ...rest } = p; return rest; });

    try {
      const [, content] = await Promise.all([progressPromise, contentPromise]);
      if (!content) throw new Error('Processing failed');
      const updated = {
        ...getUserDocs(),
        [docName]: { fileName: file.name, fileType: file.type, content, uploadedAt: new Date().toISOString() },
      };
      saveUserDocs(updated);
      setUserDocs(updated);
      clearProgress();
      showToastr('success', `${docName} uploaded.`);
    } catch (err) {
      clearProgress();
      if (err.name === 'QuotaExceededError') {
        showToastr('error', 'Storage full — delete some documents and try again.');
      } else {
        showToastr('error', 'Upload failed. Please try again.');
      }
    }
  };

  return (
    <div className="dv">
      {/* ── Header ── */}
      <div className="dv__header">
        <div className="dv__header-left">
          <FactCheckOutlinedIcon className="dv__header-icon" />
          <div>
            <h2 className="dv__title">Verify Documents</h2>
            <p className="dv__subtitle">
              Upload all required documents for{' '}
              <strong>{serviceName}</strong> to proceed.
            </p>
          </div>
        </div>
        <div className="dv__count-badge">
          <span className="dv__count-num">
            <strong>{uploadedCount}</strong>/{totalCount}
          </span>
          <span className="dv__count-label">ready</span>
        </div>
      </div>

      {/* ── Progress bar ── */}
      {totalCount > 0 && (
        <div className="dv__progress-wrap">
          <div className="dv__progress-row">
            <span>{uploadedCount} of {totalCount} documents uploaded</span>
            <span>{progressPct}%</span>
          </div>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            className="dv__progress-bar"
          />
        </div>
      )}

      {/* ── No docs required ── */}
      {totalCount === 0 && (
        <div className="dv__no-docs">
          <CheckCircleIcon className="dv__no-docs-icon" />
          <p>No specific documents required for this service.</p>
          <p className="dv__no-docs-sub">You can proceed directly to the service centers.</p>
        </div>
      )}

      {/* ── Document list ── */}
      {totalCount > 0 && (
        <div className="dv__list">
          {requiredDocs.map((doc) => {
            const uploaded = userDocs[doc];
            const isUploading = uploading[doc] !== undefined;
            const inputId = `dv-${docToId(doc)}`;

            return (
              <div
                key={doc}
                className={`dv__doc-row${uploaded ? ' dv__doc-row--done' : ''}`}
              >
                {/* Always-present hidden input — used by both Upload label and Replace button */}
                <input
                  type="file"
                  id={inputId}
                  accept="application/pdf,image/jpeg,image/png"
                  style={{ display: 'none' }}
                  ref={(el) => { fileRefs.current[doc] = el; }}
                  onChange={(e) => {
                    handleFileSelect(doc, e.target.files[0]);
                    e.target.value = '';
                  }}
                />

                <div className="dv__doc-left">
                  {uploaded
                    ? <CheckCircleIcon className="dv__status-icon dv__status-icon--done" />
                    : <RadioButtonUncheckedIcon className="dv__status-icon dv__status-icon--pending" />}
                  <span className="dv__doc-name">{doc}</span>
                </div>

                <div className="dv__doc-right">
                  {isUploading && (
                    <div className="dv__upload-progress">
                      <LinearProgress
                        variant="determinate"
                        value={uploading[doc]}
                        className="dv__upload-bar"
                      />
                      <span className="dv__upload-pct">{uploading[doc]}%</span>
                    </div>
                  )}

                  {uploaded && !isUploading && (
                    <>
                      <span className="dv__filename" title={uploaded.fileName}>
                        {uploaded.fileName}
                      </span>
                      <button
                        type="button"
                        className="dv__icon-btn"
                        title="View"
                        onClick={() => setPreviewDoc({ name: doc, ...uploaded })}
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </button>
                      <button
                        type="button"
                        className="dv__icon-btn dv__icon-btn--replace"
                        title="Replace document"
                        onClick={() => fileRefs.current[doc]?.click()}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </button>
                      <button
                        type="button"
                        className="dv__icon-btn dv__icon-btn--delete"
                        title="Remove document"
                        onClick={() => handleDelete(doc)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    </>
                  )}

                  {!uploaded && !isUploading && (
                    <label htmlFor={inputId} className="dv__upload-btn">
                      <UploadFileIcon style={{ fontSize: '0.9rem' }} />
                      Upload
                    </label>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── CTA ── */}
      <div className="dv__cta-wrap">
        {isClosed ? (
          <p className="dv__cta-hint dv__cta-hint--closed">
            This service is closed{service?.endDate ? ` — the last date (${fmtDate(service.endDate)}) has passed` : ''}.
            New applications are no longer being accepted.
          </p>
        ) : (!allUploaded && totalCount > 0 && (
          <p className="dv__cta-hint">
            Upload all {totalCount - uploadedCount} remaining document{totalCount - uploadedCount !== 1 ? 's' : ''} to continue.
          </p>
        ))}
        <button
          type="button"
          className={`dv__cta${!isClosed && (allUploaded || totalCount === 0) ? ' dv__cta--active' : ''}`}
          disabled={isClosed || (totalCount > 0 && !allUploaded)}
          onClick={() => navigate('../service-centers')}
        >
          {isClosed ? 'Service Closed' : 'Proceed to Service Centers'}
          {!isClosed && <ArrowForwardIcon className="dv__cta-icon" fontSize="small" />}
        </button>
      </div>

      {/* ── Preview overlay ── */}
      {previewDoc && (
        <div className="dv__overlay" onClick={() => setPreviewDoc(null)}>
          <div className="dv__preview" onClick={(e) => e.stopPropagation()}>
            <div className="dv__preview-head">
              <span className="dv__preview-name">{previewDoc.name}</span>
              <button type="button" className="dv__preview-close" onClick={() => setPreviewDoc(null)}>✕</button>
            </div>
            {previewDoc.fileType?.startsWith('image/') ? (
              <img src={previewDoc.content} alt={previewDoc.name} className="dv__preview-img" />
            ) : (
              <iframe src={previewDoc.content} title={previewDoc.name} className="dv__preview-iframe" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentVerification;
